// ==========================================================
// Score & Achievement Manager (LocalStorage Persistence)
// ==========================================================

export interface StageRecord {
  stageId: number;
  highScore: number;
  stars: number; // 0 to 3
  meisterTitle: string;
  clearedAt: string;
}

const STORAGE_KEY = 'paint_meister_records_v1';

export class ScoreManager {
  /**
   * Load all saved stage records from localStorage
   */
  public static getAllRecords(): Record<number, StageRecord> {
    try {
      const data = localStorage.getItem(STORAGE_KEY);
      if (!data) return {};
      return JSON.parse(data);
    } catch {
      return {};
    }
  }

  /**
   * Get record for a specific stage
   */
  public static getStageRecord(stageId: number): StageRecord | null {
    const all = this.getAllRecords();
    return all[stageId] || null;
  }

  /**
   * Save a score result for a stage. Updates only if it's a new high score or more stars.
   */
  public static saveStageScore(
    stageId: number,
    totalScore: number,
    stars: number,
    meisterTitle: string
  ): { isNewHigh: boolean; record: StageRecord } {
    const all = this.getAllRecords();
    const existing = all[stageId];

    let isNewHigh = false;
    let newScore = totalScore;
    let newStars = stars;
    let newTitle = meisterTitle;

    if (existing) {
      if (totalScore > existing.highScore) {
        isNewHigh = true;
      } else {
        newScore = existing.highScore;
      }
      newStars = Math.max(existing.stars, stars);
      if (totalScore >= existing.highScore) {
        newTitle = meisterTitle;
      } else {
        newTitle = existing.meisterTitle;
      }
    } else {
      isNewHigh = true;
    }

    const record: StageRecord = {
      stageId,
      highScore: newScore,
      stars: newStars,
      meisterTitle: newTitle,
      clearedAt: new Date().toISOString()
    };

    all[stageId] = record;

    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(all));
    } catch (e) {
      console.warn('Failed to save score to localStorage:', e);
    }

    return { isNewHigh, record };
  }

  /**
   * Calculate total stars collected across all stages
   */
  public static getTotalStars(): number {
    const all = this.getAllRecords();
    return Object.values(all).reduce((sum, r) => sum + r.stars, 0);
  }

  /**
   * Calculate cumulative high scores across all stages
   */
  public static getTotalScore(): number {
    const all = this.getAllRecords();
    return Object.values(all).reduce((sum, r) => sum + r.highScore, 0);
  }
}
